import { ConversationHistory, ConversationSummary } from '@glow/ai-contract'
import { userLanguage } from '@glow/common/utils/language'
import { Icon, IconFa, ModalConfirm } from '@glow/ui-components'
import i18next from 'i18next'
import { DateTime } from 'luxon'
import { useEffect, useRef, useState } from 'react'
import { DeleteAiConversation, FetchAiConversation, FetchAiConversations } from './aiConversations'

interface Props {
  fetchConversations: FetchAiConversations
  fetchConversation: FetchAiConversation
  deleteConversation: DeleteAiConversation
  onLoaded: (history: ConversationHistory) => void
  onDeleted: (conversationId: string) => void
  onBack: () => void
}

type ListState = { kind: 'loading' } | { kind: 'failed' } | { kind: 'loaded'; conversations: ConversationSummary[] }

// Where keyboard focus goes once the confirmation closes: back to that row, or out of a list it left
type FocusTarget = { kind: 'row'; conversationId: string } | { kind: 'back' } | null

const relativeDate = (iso: string): string => DateTime.fromISO(iso, { locale: userLanguage() }).toRelative() ?? ''

export const AiHistory = ({
  fetchConversations,
  fetchConversation,
  deleteConversation,
  onLoaded,
  onDeleted,
  onBack
}: Props) => {
  const [list, setList] = useState<ListState>({ kind: 'loading' })
  const [loadFailed, setLoadFailed] = useState(false)
  const [selecting, setSelecting] = useState(false)
  // The conversation asked about in the delete confirmation; one at a time, like selection
  const [confirming, setConfirming] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteFailed, setDeleteFailed] = useState(false)
  const [focusTarget, setFocusTarget] = useState<FocusTarget>(null)
  const backRef = useRef<HTMLButtonElement>(null)
  const deleteButtonRefs = useRef(new Map<string, HTMLButtonElement>())

  // Fetched on every mount, so reopening the history view always shows the fresh list
  useEffect(() => {
    let cancelled = false
    fetchConversations().then((result) => {
      if (!cancelled) {
        setList(result ? { kind: 'loaded', conversations: result.conversations } : { kind: 'failed' })
      }
    })
    return () => {
      cancelled = true
    }
  }, [fetchConversations])

  useEffect(() => {
    if (focusTarget?.kind === 'row') {
      deleteButtonRefs.current.get(focusTarget.conversationId)?.focus()
    } else if (focusTarget?.kind === 'back') {
      backRef.current?.focus()
    }
  }, [focusTarget])

  // One selection at a time, so a slow first click can never overwrite a later one
  const select = async (conversationId: string) => {
    setSelecting(true)
    const history = await fetchConversation(conversationId)
    if (history) {
      onLoaded(history)
    } else {
      setLoadFailed(true)
      setSelecting(false)
    }
  }

  const cancel = (conversationId: string) => {
    setConfirming(null)
    setFocusTarget({ kind: 'row', conversationId })
  }

  const remove = async (conversationId: string) => {
    setDeleting(true)
    const deleted = await deleteConversation(conversationId)
    setDeleting(false)
    setConfirming(null)
    setDeleteFailed(!deleted)
    if (deleted) {
      setList((current) =>
        current.kind === 'loaded'
          ? {
              kind: 'loaded',
              conversations: current.conversations.filter((listed) => listed.conversationId !== conversationId)
            }
          : current
      )
      setFocusTarget({ kind: 'back' })
      onDeleted(conversationId)
    } else {
      setFocusTarget({ kind: 'row', conversationId })
    }
  }

  const confirmingTitle =
    list.kind === 'loaded'
      ? (list.conversations.find((listed) => listed.conversationId === confirming)?.title ?? null)
      : null

  return (
    <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-4 py-4">
      <button
        ref={backRef}
        type="button"
        onClick={onBack}
        className="cursor-pointer self-start bg-transparent text-sm font-semibold text-primary-500"
      >
        <IconFa icon={['far', 'arrow-left']} size="sm" /> {i18next.t('aiChat.history.back')}
      </button>
      {list.kind === 'failed' && <p className="text-sm text-emphasis-medium">{i18next.t('aiChat.history.error')}</p>}
      {list.kind === 'loaded' && list.conversations.length === 0 && (
        <p className="text-sm text-emphasis-medium">{i18next.t('aiChat.history.empty')}</p>
      )}
      {list.kind === 'loaded' &&
        list.conversations.map((conversation) => (
          <div
            key={conversation.conversationId}
            className="flex items-stretch rounded border border-gray-300 hover:border-primary-500"
          >
            <button
              type="button"
              onClick={() => select(conversation.conversationId)}
              disabled={selecting}
              className="flex min-w-0 flex-1 cursor-pointer flex-col gap-0.5 px-3 py-2 text-left disabled:cursor-default disabled:opacity-60"
            >
              <span className="w-full truncate text-sm text-emphasis-high">{conversation.title}</span>
              <span className="text-xs text-emphasis-medium">{relativeDate(conversation.lastActivityAt)}</span>
            </button>
            <button
              ref={(element) => {
                if (element) {
                  deleteButtonRefs.current.set(conversation.conversationId, element)
                } else {
                  deleteButtonRefs.current.delete(conversation.conversationId)
                }
              }}
              type="button"
              onClick={() => {
                setDeleteFailed(false)
                setConfirming(conversation.conversationId)
              }}
              disabled={selecting || deleting}
              // An attribute React escapes itself; i18next's HTML escaping would show &amp; in the label
              aria-label={i18next.t('aiChat.history.delete', {
                title: conversation.title,
                interpolation: { escapeValue: false }
              })}
              className="cursor-pointer bg-transparent px-3 text-emphasis-medium hover:text-primary-500 disabled:cursor-not-allowed disabled:opacity-30"
            >
              <Icon icon="trash-alt" size="sm" />
            </button>
          </div>
        ))}
      {loadFailed && <p className="text-sm text-emphasis-medium">{i18next.t('aiChat.history.loadError')}</p>}
      {confirmingTitle !== null && confirming && (
        <ModalConfirm
          questionText={i18next.t('aiChat.history.deleteConfirm', {
            title: confirmingTitle,
            interpolation: { escapeValue: false }
          })}
          confirm={i18next.t('aiChat.history.deleteYes')}
          cancel={i18next.t('aiChat.history.deleteCancel')}
          onConfirmLoading={deleting}
          onConfirm={() => remove(confirming)}
          onClose={() => !deleting && cancel(confirming)}
        />
      )}
      {deleteFailed && (
        <p role="alert" className="text-sm text-emphasis-medium">
          {i18next.t('aiChat.history.deleteError')}
        </p>
      )}
    </div>
  )
}
